/**
 * Copyright (c) 2022 FMR LLC.
 * All Rights Reserved.
 * Fidelity Confidential Information.
 */

import { FederalFilingStatus } from "./federal-filing-status";

export const ClientConstants = {
    CALCULATE_BUTTON_ID: 'calculate-button',
    CALCULATE_BUTTON_TEXT: 'Calculate',

    YIELD_TABLE_ID: 'tax-yield-table',
    YIELD_TABLE_TITLE: 'Tax-Equivalent Yields Based on Security Type',
    EMPTY_YIELDS_TABLE: [] as string [][],
    DUMMY_YIELDS_TABLE: Array(21).fill('').map((_, iRow) => Array(5).fill('').map((_, iCol) => (1 + iRow + iCol * .01).toFixed(2) + '%')),
    APPLICABLE_TAXES_ID: 'applicable-tax-table',
    APPLICABLE_TAXES_TITLE: 'Applicable Taxes on Interest Income *',
    APPLICABLE_TAXES_FOOTNOTE: '* The impact of federal and/or state alternative minimum taxes are not reflected in this analysis.',
    SPDA_FOOTNOTE_ITEMS: [
        '# Tool conservatively considers an SPDA (Single-Premium Deferred Annuity) with a 3-year guarantee period. A longer guarantee period may provide a better tax equivalent yield. Click',
        'here',
        'for SPDA products available through Fidelity Insurance Network.',
    ],

    CD_COLUMN_HEADING: 'CDs',
    CORPORATE_COLUMN_HEADING: 'Corporate',
    IN_STATE_MUNICIPAL_COLUMN_HEADING: 'In-State Municipal',
    OUT_OF_STATE_MUNICIPAL_COLUMN_HEADING: 'Out-of-State Municipal',
    TREASURY_AGENCY_COLUMN_HEADING: 'Treasury and Agency/GSE',
    SPDA_COLUMN_HEADING: '3-Year Single Premium Deferred Annuity',

    CD_APPLICABLE_TAXES_INFO: 'Federal, State & Local',
    CORPORATE_APPLICABLE_TAXES_INFO: 'Federal, State & Local',
    IN_STATE_MUNICIPAL_APPLICABLE_TAXES_INFO: 'NONE',
    OUT_OF_STATE_MUNICIPAL_APPLICABLE_TAXES_INFO: 'State & Local',
    TREASURY_AGENCY_APPLICABLE_TAXES_INFO: 'Federal',
    SPDA_APPLICABLE_TAXES_INFO: 'Deferred Federal, State & Local',

    TAXES_TITLE: 'Taxes',
    TAXES_FEDERAL_TAX_FILING_STATUS_INPUT_LABEL_TEXT: 'Federal Tax Filing Status',
    TAXES_PRINCIPAL_STATE_OF_RESIDENCE_INPUT_LABEL_TEXT: 'Principal State of Residence',
    TAXES_LOCAL_INCOME_TAX_INPUT_LABEL_TEXT: 'Local Income Tax',

    //Input field errors
    FEDERAL_TAX_FILING_STATUS_ERROR: 'Federal Tax Filing Status is required.',
    PRINCIPAL_STATE_OF_RESIDENCE_ERROR: 'Principal State of Residence is required.',
    LOCAL_INCOME_TAX_ERROR: 'Local Income Tax is not in the range 0.00 through 15.00.',

    // Dropdown values
    filingStatusList: [
        {value: 'invalid', text: 'Select a filing status'},
        {value: FederalFilingStatus.S, text: 'Single'},
        {value: FederalFilingStatus.MFJ, text: 'Married Filing Jointly'},
        {value: FederalFilingStatus.MFS, text: 'Married Filing Separately'},
        {value: FederalFilingStatus.HH, text: 'Head of Household'},
        {value: FederalFilingStatus.QW, text: 'Qualified Widower/Dependent Child'},
    ],
    
    stateOfResidenceList: [
        {value:'invalid', text: 'Select a state'},
        {value: 'AL', text: 'Alabama'},
        {value: 'AK', text: 'Alaska'},
        {value: 'AZ', text: 'Arizona'},
        {value: 'AR', text: 'Arkansas'},
        {value: 'CA', text: 'California'},
        {value: 'CO', text: 'Colorado'},
        {value: 'CT', text: 'Connecticut'},
        {value: 'DE', text: 'Delaware'},
        {value: 'DC', text: 'District of Columbia'},
        {value: 'FL', text: 'Florida'},
        {value: 'GA', text: 'Georgia'},
        {value: 'HI', text: 'Hawaii'},
        {value: 'ID', text: 'Idaho'},
        {value: 'IL', text: 'Illinois'},
        {value: 'IN', text: 'Indiana'},
        {value: 'IA', text: 'Iowa'},
        {value: 'KS', text: 'Kansas'},
        {value: 'KY', text: 'Kentucky'},
        {value: 'LA', text: 'Louisiana'},
        {value: 'ME', text: 'Maine'},
        {value: 'MD', text: 'Maryland'},
        {value: 'MA', text: 'Massachusetts'},
        {value: 'MI', text: 'Michigan'},
        {value: 'MN', text: 'Minnesota'},
        {value: 'MS', text: 'Mississippi'},
        {value: 'MO', text: 'Missouri'},
        {value: 'MT', text: 'Montana'},
        {value: 'NE', text: 'Nebraska'},
        {value: 'NV', text: 'Nevada'},
        {value: 'NH', text: 'New Hampshire'},
        {value: 'NJ', text: 'New Jersey'},
        {value: 'NM', text: 'New Mexico'},
        {value: 'NY', text: 'New York'},
        {value: 'NC', text: 'North Carolina'},
        {value: 'ND', text: 'North Dakota'},
        {value: 'OH', text: 'Ohio'},
        {value: 'OK', text: 'Oklahoma'},
        {value: 'OR', text: 'Oregon'},
        {value: 'PA', text: 'Pennsylvania'},
        {value: 'RI', text: 'Rhode Island'},
        {value: 'SC', text: 'South Carolina'},
        {value: 'SD', text: 'South Dakota'},
        {value: 'TN', text: 'Tennessee'},
        {value: 'TX', text: 'Texas'},
        {value: 'UT', text: 'Utah'},
        {value: 'VT', text: 'Vermont'},
        {value: 'VA', text: 'Virginia'},
        {value: 'WA', text: 'Washington'},
        {value: 'WV', text: 'West Virginia'},
        {value: 'WI', text: 'Wisconsin'},
        {value: 'WY', text: 'Wyoming'},
    ],
    BOND_OPTIONS: [
        { value: 'invalid', text: 'Select a product type', dataColumn:'invalid' },
        { value: 'CD', text: 'Certificate of Deposit', dataColumn:'cdYields'},
        { value: 'CO', text: 'Corporate Bond', dataColumn:'corpYields'},
        { value: 'ISM', text: 'In-State Municipal',dataColumn:'inStateMuniYields' },
        { value: 'OSM', text: 'Out-of-State Municipal',dataColumn:'outOfStateMuniYields' },
        { value: 'TA', text: 'Treasury and Agency/GSE', dataColumn:'treasuryAgencyYields'},
        { value: 'SPDA', text: '3-Year Single Premium Deferred Annuity', dataColumn: 'spdaYields',}
    ],
    BOND_SECTION_LABEL: 'Product Information',
    BOND_INPUT_LABEL: 'Product Type',
    BOND_INFORMATION_CONTENT: 'To compare a product with other product types, enter the product type and yield:',
    BOND_INLINE_ERROR: 'Select a product type',
    YIELD_INPUT_LABEL: 'Yield',
    YIELD_POPOVER_CONTENT: 'The yields presented in this calculator are annual rates of return, expressed as a percentage. Yields, which are calculated using a number of factors including the principal, the maturity, and the coupon, are one of the primary measuring sticks for an income product.',
    YIELD_INLINE_ERROR: 'Enter a yield between 0% and 50%',

    HOUSEHOLD_INCOME_INPUT_TITLE: ' Household Income',
    HOUSEHOLD_INCOME_LABEL_TEXT: 'Estimated Taxable Income',
    HOUSEHOLD_INCOME_INPUT_CONTENT: `Estimated Taxable Income is used by the calculator to find applicable federal and state marginal income tax rates. You may want to enter your taxable income from last year's federal income tax return as an estimate for this year.`,
    HOUSEHOLD_INCOME_INPUT_CHECKBOX: 'If you do not want to enter your Estimated Taxable Income, check this box to use the highest marginal tax rates for both federal and applicable state taxes. Note that the highest rate will tend to favor tax-exempt bonds.',
    HOUSEHOLD_INCOME_INPUT_ERROR_MESSAGE: 'Estimated Taxable Income is not in the range 0 through 99,999,999.',

    INFO_WE_CALCULATED_SECTION_ID: 'info-we-calculated-section',
    INFO_WE_CALCULATED_ID: 'info-we-calculated',
    INFO_WE_CALCULATED_TITLE: 'Information We Calculated',
    FEDERAL_TAX_RATE_LABEL: 'Federal Tax Rate',
    STATE_TAX_RATE_LABEL: 'State Tax Rate',

    MICROCOPY_REQUIRED_TAG: '(Required)',
    MICROCOPY_LOCAL_INCOME_TEXT: '(If you know you pay local income tax, \n' +  '\n enter the rate here. Otherwise, leave this field blank.)',
    // urls
    FEDERAL_TAX_URL: '/prgw/digital/taxyieldcalc/api/taxes/federal',
    STATE_TAX_URL: '/prgw/digital/taxyieldcalc/api/taxes/state',
    CALCULATIONS_URL: '/prgw/digital/taxyieldcalc/api/calculations',
    GLANCE_URL_DEV: 'https://dmt.fidelity.com/dev/meas/',
    GLANCE_URL_QA: 'https://dmt.fidelity.com/stage/meas/',
    GLANCE_URL_PROD: 'https://dmt.fidelity.com/prod/meas/',
    FIDELITY_URL: 'https://www.fidelity.com',

    // error messages
    FEDERAL_TAX_REQUEST_ERROR: 'Get federal taxes request failed',
    STATE_TAX_REQUEST_ERROR: 'Get all state taxes request failed',
    CALCULATIONS_REQUEST_ERROR: 'Get yields request failed',
    MISSING_RESPONSE_DATA: 'Missing response data',

    // sidebar and header content
    SIDEBAR_TITLE: 'About Products and Taxes',
    SIDEBAR_PARAGRAPHS: [
        'The tax treatment of a bond\'s interest income (the coupon payments) varies according to what type of bond it is and where it is issued.',
        'However, tax-advantaged bonds generally offer a lower coupon rate than equivalent taxable bonds, such as corporate bonds.',
        'Although interest income from some bonds may be exempt from federal, state, and/or local taxes, capital gains and losses are generally taxable at all levels.',
        'Interest income from Bonds & CDs are taxed in the year they are distributed. On the other hand, SPDA interest grows tax-deferred until withdrawn. Additionally, SPDA distributions are taxable at all levels.',
        'Possibility of Adverse Tax Consequences on SPDA:', 
        'A penalty tax equal to 10% of the amount treated as taxable income may be imposed on SPDA distributions received before age 59½. In the calculator, we’ve assumed all distributions occur after reaching age 59½.',
    ],
    SIDEBAR_LIST_ITEMS: [
        'Income from a U.S. Treasury bond is free from state and local taxes.',
        'Income from a municipal bond, or muni, is usually exempt from regular federal income tax (but may be subject to the alternative minimum tax in some cases).',
        'Income from some munis available to you may not be taxed at all, particularly those issued by the state in which you file.',
    ],
    ASSUMPTIONS_MODAL_LINK: 'See our Assumptions',
    ASSUMPTIONS_MODAL_CONTENT: {
        title: 'Assumptions of this Calculator',
        paragraphOne: 'The federal and state tax rates assumed by this calculator in calculating tax equivalent yields are disclosed with your results. While reasonable efforts are made to timely update tax rates and income tax brackets used by this calculator, there may be a time lag between those assumed by the tool and actual ones. Note that information for certain states may not become available until the middle of the applicable tax year. You should review the assumed tax rates against your actual tax rates based on your individual tax situation when analyzing and interpreting this calculator\'s results.',
        listOneTitle: 'The calculator assumes that:',
        listOneItems: [
            'All bonds remain at par value, so the effect of capital gains or losses is not reflected',
            'Bonds, except CDs, are assumed to pay coupons semi-annually',
            'CD yields are assumed to be a true annual yield, reflecting compounding of interest',
            'Bond yields, except CDs, are assumed to be twice the semi-annual yield, as is the normal convention for quoting bond yields. CD yield is calculated as ((( corporate bond yield / 2) +1)\u{00B2} ) - 1',
            '3-Year SPDA yield reflects tax-deferred compounded interest which is taxed at federal, state, and local level',
            '3-Year SPDA yield is calculated as (((CD yield)\u{00B3} (Total tax – 1)\u{00B2} – 3 (CD yield)\u{00B2} (Total tax -1) + 3 (CD Yield) +1)\u{2153} -1)',
        ],
        listTwoTitle: 'The calculator does not take into account:',
        listTwoItems: [
            'Reductions and limits on federal itemized deductions',
            'State and local taxes are not deducted from your federal tax rate. Depending on your personal situation, this may cause the resulting yield to be overstated.',
            'Federal alternative minimum tax (AMT)',
            'State alternative minimum tax',
            'Intangibles taxes levied by individual states',
            'Net Investment Income Tax',
            'Additional Medicare Tax',
            'Availability of products and rate guarantee periods',
        ],
    },
    TERMS_MODAL_LINK: 'Terms and Conditions of Use',
    TERMS_MODAL_CONTENT: {
        title: 'Terms and Conditions of Use',
        paragraphOne: `The Tax-Equivalent Yield Calculator is made available on an as-is, as-available basis. Tax-Equivalent Yield Calculator content is general in nature, is provided for informational purposes only and should not be construed as legal, tax, or investment advice. Fidelity cannot guarantee that the content is accurate, complete, or timely, or that the calculator produces accurate and/or complete results. Laws of a particular state or laws that may be applicable to a particular situation may have an impact on the applicability, accuracy, or completeness of information provided. Federal and state laws and regulations are complex and are subject to change. Changes in such laws and regulations may have a material impact on pretax and/or after-tax investment results. Fidelity makes no warranties with regard to, bears no responsibility for, and disclaims any liability arising out of, an investor's use (or the results obtained from, interpretations made as a result of, or any tax position taken in reliance on information provided pursuant to, such investor's use) of the calculator. An investor should contact an attorney, tax advisor, or investment professional regarding the investor's specific legal, tax, and/or investment situation before taking any action based on the information provided.`,
    },

    NEXT_STEPS_SIDEBAR: {
        nextStepsTitle: 'Next Steps',
        nextStepsLinks: [
            {
                title: 'Search Our Bond Offerings',
            },
            {
                title: 'Tax Implications',
            },
            {
                title: 'Search Deferred Fixed Annuities',
            }
        ],
    },

    HEADER_TEXT: 'Taxes vary according to the type of product you own. To compare a product yield to the equivalent yield for other products, enter the information below. The impact of federal and/or state alternative minimum taxes (AMTs) are not reflected in this analysis. ',
    TABLE_INFORMATION_TEXT: [
        'This table compares',
        'Tax-Equivalent Yields',
        ', the before-tax yields needed for different product types to have the same after-tax value. Thus, the higher a product\'s displayed yield, the more taxes incurred. Your product is highlighted in green.'
    ],
    LEARN_MORE_MODAL_LINK:' Learn more.',

    LEARN_MORE_MODAL_SECTIONS: [
        {
            title: 'About This Calculator',
            paragraphs: [
                'When investing in bonds & annuities, it\'s important to understand the different tax treatments applied to the different kinds of products. For example, income from most municipal bonds, or munis, is exempt from regular federal income tax (but may be subject to the alternative minimum tax). Likewise, income from a U.S. Treasury bond is free from state and local income taxes. These exemptions apply only to the interest paid by these bonds. Earnings from any capital gains are still taxable.',
                'Tax-Equivalent Yield is one means of comparing different bond types while taking into account some of their respective tax advantages. Tax-Equivalent Yield is defined as pretax yield that must be received on a taxable security to provide the holder the same after-tax yield as that earned on a tax-exempt security. Please note that the calculator does not take into account the federal AMT.',
                'Based on the characteristics of the product you enter, this calculator provides the Tax-Equivalent Yield for a variety of other bond types. It is intended to help you make an informed decision about investing in bonds & annuities.',
                'It\'s important to note that, while munis and U.S. Treasury bonds offer appealing tax advantages, these breaks come at a cost. These products usually offer a lower coupon rate than equivalent taxable bonds. Many corporate bonds, for example, offer higher coupon rates to compensate for the fact that the interest they pay is subject to income tax at the local, state, and federal level. Generally speaking, the higher your income tax bracket, the more advantageous the after-tax yield of munis may be for you.',
                'When considering different bonds, the tax-equivalent yield is one of a number of factors to consider (others being, for example, quality rating and term to maturity).  Moreover, when comparing across different income products, consider the type of guarantee, ability to access assets, and trading costs or fees (including early or excess withdrawal charges in the case of SPDAs). Of course, as with any investment, your decision should take into account your personal investment objectives, needs, comfort with risk, and time horizon.'
            ]
        },
        {
            title: 'Viewable Information',
            paragraphs: [
                'The in-state municipal bond column provides the yield of a municipal bond that should be exempt from federal, state, and local income taxes for in-state taxpayers.',
                'The out-of-state municipal bond column provides the yield necessary from an out-of-state municipal bond to equal the corresponding in-state municipal bond yield. Interest on these securities for taxpayers residing in a state other than the state where the borrower issued the bond should be exempt from federal income taxes but is usually subject to state and local income taxes.',
                'The Treasury bond column provides the yield necessary from a Treasury bond to equal the in-state municipal bond yield. Interest on these securities should be subject to federal income taxes but exempt from state and local income taxes.',
                'The corporate bond column provides the yield necessary from a corporate bond to equal the in-state municipal bond yield. Interest on these securities should be subject to federal, state, and local income taxes.',
                'The 3-Year SPDA column provides the yield necessary from a 3-year Single Premium Deferred Annuity to equal the in-state municipal bond yield. Interest on SPDAs will be subject to federal, state, and local income taxes. For this tool, the 3-Year SPDA is considered as it is the SPDA with the shortest guarantee period available at issue and demonstrates the most conservative tax equivalent yield. SPDAs with longer guarantee periods are also available, which may provide better tax equivalent yield.',
            ]
        },
        {
            title: 'Tax Treatment of Product Types in Calculator',
            paragraphs: [ 'Calculating tax-equivalent yield requires knowing what the total effective income tax rates are on each type of bond. They have been calculated as follows:', ]
        },
        {
            title: 'Corporate Bond Tax Rate - ',
            paragraphs: [
                ' This is the effective total marginal income tax rate on interest income from corporate bonds, including federal, state, and local income taxes. Note that the corporate bond tax rate is also applicable to CDs and SPDAs. The effective total marginal income tax rate on interest income from corporate bonds has been calculated as follows:',
                'Corporate Bond Tax Rate = marginal federal income tax rate + marginal state income tax rate + marginal local income tax rate',
            ]
        },
        {
            title: 'Treasury Bond Tax Rate - ',
            paragraphs:[
                'This is the effective total marginal income tax rate on interest income from Treasury bonds, including federal, state, and local income taxes. The Treasury Bond Tax Rate is applicable to Treasury and certain government agency and government-sponsored enterprise (GSE) bonds. These bonds should be subject to federal income taxes, but may not be subject to state and local income taxes.',
                'The effective total marginal income tax rate on interest income from Treasury, agency, and GSE bonds should therefore equal the marginal federal income tax rate.',
            ]
        },
        {
            title: 'Out-of-State Municipal Bond Tax Rate - ',
            paragraphs: [
                'This is the effective total marginal income tax rate on interest income from out-of-state municipal bonds, including federal, state, and local income taxes. Out-of-state municipal bonds are generally subject to state and local income taxes, but not federal income taxes. Therefore, the calculator assumes that there is no federal income tax, but that there are state and local income taxes. The effective total marginal income tax rate on interest income from out-of-state municipal bonds should equal:',
                'Out-of-State Municipal Bond Tax Rate = marginal state income tax rate + marginal local income tax rate',
            ]
        },
        {
            title: 'In-State Municipal Bond Tax Rate - ',
            paragraphs: [
                'This is the effective total marginal income tax rate on interest income from in-state municipal bonds, including federal, state, and local income taxes. In-state municipal bonds are generally not subject to federal, state, or local income taxes. Therefore, the calculator assumes there is no federal, state, or local income tax.',
                'The effective total marginal income tax rate on interest income from in-state municipal bonds should, therefore, be zero.',
            ]
        },
        {
            title: 'How Tax Equivalent Yields Are Calculated',
            paragraphs: [
                'The Tax-Equivalent Yield Calculator uses the following formulas to calculate tax-equivalent yields:',
                'Users indicate their estimated taxable income and federal income tax filing status, from which the calculator will determine a marginal federal ordinary income tax rate. Or, users may indicate that they wish to use the highest marginal federal ordinary income tax rate. Users also indicate their state of residence and local income tax rate, if any. Various adjustments have been made to take into account deductions of state taxes from federal taxable income and, if appropriate, deductions of federal or state income taxes from state taxable income.',
                'This calculator does not factor in any state or local tax deductions from the federal tax rate.',
            ],
            list: [
                {
                    bond: 'Out-of-State Municipal Bond Yield = ',
                    formula: 'In-State Muni Bond Yield / (1 – Out-of-State Muni Bond Tax Rate)',
                },
                {
                    bond: 'Treasury Bond Yield = ',
                    formula: 'In-State Muni Bond Yield / (1 – Treasury Bond Tax Rate)',
                },
                {
                    bond: 'Corporate Bond Yield = ',
                    formula: 'In-State Muni Bond Yield / (1 – Corporate Bond Tax Rate)',
                },
                {
                    bond: 'CD Bond Yield = ',
                    formula: '(((Corporate Bond Yield / 2) + 1)\u{00B2} ) - 1',
                },
                {
                    bond: '3-Year SPDA = ',
                    formula: '(((CD Bond Yield)\u{00B3} (Corporate Bond Tax Rate - 1)\u{00B2} – 3 (CD Bond Yield)\u{00B2} (Corporate Bond Tax Rate - 1) + 3 (CD Bond Yield) + 1)\u{2153} - 1)',
                },
            ] ,
        },
    ],

    FEDERAL_TAX_LOOKUP_ERROR: (filingStatus: string, annualIncome: string) =>
        `Unable to match federal tax rate for filing status ${filingStatus} and income ${annualIncome}`,
    MAX_FEDERAL_TAX_LOOKUP_ERROR: (filingStatus: string) =>
        `Unable to match maximum federal tax rate for filing status ${filingStatus}`,
    STATE_TAX_LOOKUP_ERROR: (state: string, filingStatus: string, annualIncome: string) =>
        `Unable to match state tax rate for state ${state}, filing status ${filingStatus}, and income ${annualIncome}`,
    MAX_STATE_TAX_LOOKUP_ERROR: (state: string, filingStatus: string) =>
        `Unable to match maximum state tax rate for state ${state} and filing status ${filingStatus}`,
}